/**
 * Zustand 全局状态管理
 *
 * 管理设备状态、遥测数据、控制权等全局状态
 * 协议版本: V2.0 (v1.0.0_20260105)
 */

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

// ============================================================
// 类型定义 (V2.0协议)
// ============================================================

/** 地理坐标 */
export interface GeoLocation {
  lon: number
  lat: number
  alt: number
}

/** 吊舱信息 */
export interface PotInfo {
  pot_lat: number
  pot_lon: number
  pot_alti: number
  servo_az: number
  servo_el: number
  servo_ra: number
  temperature?: number // 吊舱温度(°C)
  laser_dis: number
  vis_fov: number
  ir_fov: number
}

/** 检测目标 */
export interface DetectTarget {
  id: number
  cls: number
  conf: number
  rect: number[]
  geo?: GeoLocation
  miss?: { x: number; y: number }
  size?: { len: number; wid: number; hight: number }
}

/** 声学目标 (V2.0: 角度单位为度) */
export interface SoundTarget {
  type: number
  az: number      // 方位角(度)
  el: number      // 俯仰角(度)
  dist: number
  conf: number
  geo?: GeoLocation
}

/** 无线电目标 (V2.0: 角度单位为度) */
export interface RadioTarget {
  type: number
  dist: number
  dir: number     // 测向(度)
}

/** 设备健康状态 (V2.0: 0=损坏, 1=正常) */
export interface HealthStatus {
  vis_cam: number   // 可见光相机: 0=损坏, 1=正常
  ir_cam: number    // 红外相机: 0=损坏, 1=正常
  proc_bd: number   // 处理板: 0=损坏, 1=正常
  sound: number     // 声传感器: 0=损坏, 1=正常
  radio: number     // 无线电: 0=损坏, 1=正常
  servo: number     // 伺服: 0=损坏, 1=正常
  laser: number     // 测距仪: 0=损坏, 1=正常
}

/** 相机运行参数状态 (V2.0协议中的vi_conf/ir_conf) */
export interface CameraConfStatus {
  roi_mode: number       // ROI模式: 0=固定区域(全屏), 1=窗口选取
  roi_rect: number[]     // ROI坐标 [x, y, w, h]
  zoom_opt: number       // 光学变倍值
  zoom_ele: number       // 电子变倍值
  model_name: string     // 当前使用的模型名称
  bitratepu_push: number // 推流码率(MHz)
  bitratepu_save: number // 保存码率(MHz)
  osd_st: number         // OSD状态: 0=关闭, 1=开启
  work_st: number        // 工作状态: 0=空闲, 1=检测, 2=跟踪, 3=传感器联合定位
  track_st: number       // 跟踪状态: 0=丢失, 1=正在跟踪, 2=记忆跟踪
  detect_en: number      // 检测开关: 0=关闭, 1=切片检测, >=2=全图检测
  detect_type: Record<string, number>  // 检测类型开关
}

/** 设备状态 (V2.0协议) */
export interface DeviceStatus {
  // 设备位置（用于Web端地图标记）
  self_pos: number[]    // [纬度, 经度]

  // 视场角区域顶点（如果后端提供）
  fov?: [number, number][]

  // 当前流媒体源标识 (V2.0: 0=可见光, 1=红外)
  src_type: number

  // 硬件健康状态
  health: HealthStatus

  // 相机运行参数
  vi_conf?: CameraConfStatus   // 可见光运行参数(src_type=0时有效)
  ir_conf?: CameraConfStatus   // 红外运行参数(src_type=1时有效)

  // 吊舱信息
  pot_info?: PotInfo
}

/** 检测数据 */
export interface DetectData {
  count: { vis: number; ir: number; snd: number; rad: number }
  vis: DetectTarget[]
  ir: DetectTarget[]
  snd: SoundTarget[]
  rad: RadioTarget[]
}

/** 遥测数据 (V2.0协议) */
export interface TelemetryData {
  ver: string
  type: string
  dev_id: string
  seq: number
  ts: number
  status: DeviceStatus
  detect: DetectData
}

/** 设备信息 */
export interface DeviceInfo {
  dev_id: string
  name?: string
  status: 'online' | 'offline'
  last_seen?: string
  recording: {
    vis: boolean
    ir: boolean
  }
  streams: {
    vis?: string
    ir?: string
  }
}

/** 控制锁状态 */
export interface LockState {
  locked: boolean
  owner_id?: string
  owner_name?: string
  expire_ts?: number
}

// ============================================================
// 设备状态 Store
// ============================================================

interface DeviceState {
  // 设备列表
  devices: DeviceInfo[]
  // 当前选中的设备
  selectedDeviceId: string | null
  // 设备的最新遥测数据 {dev_id: TelemetryData}
  telemetryMap: Record<string, TelemetryData>
  // 前端实际收到该设备遥测的时间戳（毫秒）
  telemetryReceivedAtMap: Record<string, number>
  // 设备控制锁状态 {dev_id: LockState}
  lockMap: Record<string, LockState>
  // WebSocket 连接状态
  wsConnected: boolean

  // Actions
  setDevices: (devices: DeviceInfo[]) => void
  selectDevice: (devId: string | null) => void
  updateTelemetry: (data: TelemetryData) => void
  updateLockState: (devId: string, state: LockState) => void
  updateRecordingState: (devId: string, recording: DeviceInfo['recording']) => void
  setWsConnected: (connected: boolean) => void
}

export const useDeviceStore = create<DeviceState>()(
  devtools(
    (set) => ({
      devices: [],
      selectedDeviceId: null,
      telemetryMap: {},
      telemetryReceivedAtMap: {},
      lockMap: {},
      wsConnected: false,

      setDevices: (devices) => set({ devices }),

      selectDevice: (devId) => set({ selectedDeviceId: devId }),

      updateTelemetry: (data) =>
        set((state) => ({
          telemetryMap: {
            ...state.telemetryMap,
            [data.dev_id]: data,
          },
          telemetryReceivedAtMap: {
            ...state.telemetryReceivedAtMap,
            [data.dev_id]: Date.now(),
          },
        })),

      updateLockState: (devId, lockState) =>
        set((state) => ({
          lockMap: {
            ...state.lockMap,
            [devId]: lockState,
          },
        })),

      updateRecordingState: (devId, recording) =>
        set((state) => ({
          devices: state.devices.map((device) =>
            device.dev_id === devId
              ? { ...device, recording: { vis: !!recording.vis, ir: !!recording.ir } }
              : device
          ),
        })),

      setWsConnected: (connected) => set({ wsConnected: connected }),
    }),
    { name: 'device-store' }
  )
)

// ============================================================
// 用户状态 Store
// ============================================================

interface UserState {
  // 用户信息
  userId: string | null
  username: string | null
  role: 'admin' | 'user' | null
  // 认证 Token
  token: string | null

  // Actions
  setUser: (userId: string, username: string, role: 'admin' | 'user', token: string) => void
  logout: () => void
  isAdmin: () => boolean
  // 初始化：从 localStorage 恢复状态后验证 Token 有效性
  initAuth: () => Promise<boolean>
}

export const useUserStore = create<UserState>()(
  devtools(
    persist(
      (set, get) => ({
        userId: null,
        username: null,
        role: null,
        token: null,

        setUser: (userId, username, role, token) => {
          set({ userId, username, role, token })
        },

        logout: () => {
          set({ userId: null, username: null, role: null, token: null })
        },

        isAdmin: () => get().role === 'admin',

        initAuth: async () => {
          const { token } = get()
          if (!token) {
            return false
          }

          // 验证 Token 是否有效
          try {
            const API_BASE = import.meta.env.VITE_API_BASE || ''
            const response = await fetch(`${API_BASE}/api/v1/auth/verify`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            })

            if (!response.ok) {
              // Token 无效，清除登录状态
              get().logout()
              return false
            }

            return true
          } catch {
            // 网络错误，保持当前状态（可能是离线模式）
            return true
          }
        },
      }),
      {
        name: 'user-storage', // localStorage 的 key
        partialize: (state) => ({
          // 只持久化这些字段
          userId: state.userId,
          username: state.username,
          role: state.role,
          token: state.token,
        }),
      }
    ),
    { name: 'user-store' }
  )
)

// ============================================================
// UI 状态 Store
// ============================================================

/** 地图中心配置 */
export interface MapCenterConfig {
  lon: number
  lat: number
  zoom: number
}

export interface AlarmRecord {
  id: number
  dev_id: string
  level: 'warning' | 'danger'
  code: number
  msg: string
  created_at: string
  handled: boolean
  target_geo?: {
    lon: number
    lat: number
    alt?: number
  } | null
}

interface UIState {
  // 侧边栏折叠状态
  siderCollapsed: boolean
  // 当前播放的视频源
  activeVideoSource: 'vis' | 'ir'
  // 控制面板是否展开
  controlPanelExpanded: boolean
  // 地图中心配置
  mapCenter: MapCenterConfig
  // 报警列表
  alarms: AlarmRecord[]

  // Actions
  toggleSider: () => void
  setActiveVideoSource: (source: 'vis' | 'ir') => void
  toggleControlPanel: () => void
  setMapCenter: (center: MapCenterConfig) => void
  setAlarms: (alarms: AlarmRecord[]) => void
  addAlarm: (alarm: AlarmRecord) => void
  addAlarms: (alarms: AlarmRecord[]) => void
  markAlarmHandled: (alarmId: number) => void
  removeAlarm: (alarmId: number) => void
  clearAlarms: () => void
}

export const useUIStore = create<UIState>()(
  devtools(
    (set) => ({
      siderCollapsed: false,
      activeVideoSource: 'vis',
      controlPanelExpanded: true,
      mapCenter: { lon: 116.397428, lat: 39.90923, zoom: 15 }, // 默认北京
      alarms: [],

      toggleSider: () =>
        set((state) => ({ siderCollapsed: !state.siderCollapsed })),

      setActiveVideoSource: (source) => set({ activeVideoSource: source }),

      toggleControlPanel: () =>
        set((state) => ({ controlPanelExpanded: !state.controlPanelExpanded })),

      setMapCenter: (center) => set({ mapCenter: center }),

      addAlarm: (alarm) =>
        set((state) => ({
          alarms: upsertAlarmRecords(state.alarms, [alarm]),
        })),

      addAlarms: (alarms) =>
        set((state) => ({
          alarms: upsertAlarmRecords(state.alarms, alarms),
        })),

      setAlarms: (alarms) =>
        set((state) => ({
          alarms: upsertAlarmRecords([], alarms).map((alarm) => {
            const existing = state.alarms.find((item) => item.id === alarm.id)
            return existing ? { ...existing, ...alarm } : alarm
          }),
        })),

      markAlarmHandled: (alarmId) =>
        set((state) => ({
          alarms: state.alarms.map((alarm) =>
            alarm.id === alarmId ? { ...alarm, handled: true } : alarm
          ),
        })),

      removeAlarm: (alarmId) =>
        set((state) => ({
          alarms: state.alarms.filter((alarm) => alarm.id !== alarmId),
        })),

      clearAlarms: () => set({ alarms: [] }),
    }),
    { name: 'ui-store' }
  )
)

function upsertAlarmRecords(existing: AlarmRecord[], incoming: AlarmRecord[]) {
  const alarmMap = new Map<number, AlarmRecord>()

  for (const alarm of existing) {
    alarmMap.set(alarm.id, alarm)
  }

  for (const alarm of incoming) {
    const prev = alarmMap.get(alarm.id)
    alarmMap.set(alarm.id, prev ? { ...prev, ...alarm } : alarm)
  }

  return Array.from(alarmMap.values())
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )
    .slice(0, 200)
}
